import {BaseComponent} from '../base/component.base.js';
import template from "./login.template.html";
import styles from "./login.styles.css";
import {getLocale, setLocale, t} from '../../i18n/i18n.js';

export class LoginComponent extends BaseComponent {
    #isRegister = false;
    #loading = false;
    #slowHintTimeoutId = null;

    /**
     * The shared translation pass resets the heading, submit label and mode link to their
     * login-mode texts, so they are set again for the current mode and, while a request runs,
     * for its loading state, along with the status line.
     */
    #onLocaleChange = () => {
        this.#syncLocaleButtons();
        const heading = this.$('#heading');
        const toggleLink = this.$('#toggle-mode');
        if (heading) heading.textContent = this.#isRegister ? t('login.headingRegister') : t('login.heading');
        if (toggleLink) toggleLink.textContent = this.#isRegister ? t('login.toggleToLogin') : t('login.toggleToRegister');
        this.#setSubmitLabel(this.#submitLabel());
        const status = this.$('#auth-status');
        if (this.#loading && status) {
            status.textContent = status.classList.contains('slow')
                ? t('login.slowHint')
                : (this.#isRegister ? t('login.statusRegister') : t('login.statusLogin'));
        }
    };

    /** @returns {string} the submit button's text for the current mode and loading state */
    #submitLabel() {
        if (this.#loading) {
            return this.#isRegister ? t('login.loadingRegister') : t('login.loadingLogin');
        }
        return this.#isRegister ? t('login.submitRegister') : t('login.submitLogin');
    }

    connectedCallback() {
        this.loadTemplate(template, styles);
        document.addEventListener('locale-change', this.#onLocaleChange);

        const form = this.$('#auth-form');
        const toggleLink = this.$('#toggle-mode');
        const clientSlugField = this.$('#client-slug-field');
        const submitBtn = this.$('#submit-btn');
        const heading = this.$('#heading');
        const forgotButton = this.$('#forgot-password');
        const forgotHelp = this.$('#forgot-password-help');

        // The header's language switch is only shown after login, so the login screen has its own.
        this.$$('.btn-locale').forEach((btn) => {
            btn.addEventListener('click', () => setLocale(btn.dataset.locale));
        });
        this.#syncLocaleButtons();

        forgotButton.addEventListener('click', () => {
            const open = !forgotHelp.classList.toggle('hidden');
            forgotButton.setAttribute('aria-expanded', String(open));
        });

        toggleLink.addEventListener('click', (e) => {
            e.preventDefault();
            if (this.#loading) return;
            this.#isRegister = !this.#isRegister;
            clientSlugField.classList.toggle('hidden', !this.#isRegister);
            const clientSlugInput = this.$('#clientSlug');
            const guidInput = this.$('#userCreationGuid');
            if (this.#isRegister) {
                clientSlugInput.setAttribute('required', '');
                guidInput.setAttribute('required', '');
            } else {
                clientSlugInput.removeAttribute('required');
                guidInput.removeAttribute('required');
            }
            // Registration sets a new password, so the lost-password hint only belongs to login.
            forgotButton.classList.toggle('hidden', this.#isRegister);
            forgotHelp.classList.add('hidden');
            forgotButton.setAttribute('aria-expanded', 'false');
            this.#setSubmitLabel(this.#isRegister ? t('login.submitRegister') : t('login.submitLogin'));
            heading.textContent = this.#isRegister ? t('login.headingRegister') : t('login.heading');
            toggleLink.textContent = this.#isRegister ? t('login.toggleToLogin') : t('login.toggleToRegister');
        });

        form.addEventListener('submit', (e) => {
            e.preventDefault();
            if (this.#loading) {
                return;
            }

            const email = form.email.value;
            const password = form.password.value;
            this.#setLoading(true);

            if (this.#isRegister) {
                const clientSlug = form.clientSlug.value;
                const userCreationGuid = form.userCreationGuid.value;
                this.emit('auth-register', {email, password, clientSlug, userCreationGuid});
            } else {
                this.emit('auth-login', {email, password});
            }
        });

        document.addEventListener('auth-request-complete', this.#onAuthRequestComplete);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('auth-request-complete', this.#onAuthRequestComplete);
        document.removeEventListener('locale-change', this.#onLocaleChange);
        this.#clearSlowHint();
    }

    /** Marks the language button of the current locale, visibly and as pressed. */
    #syncLocaleButtons() {
        const current = getLocale();
        this.$$('.btn-locale').forEach((btn) => {
            btn.classList.toggle('active', btn.dataset.locale === current);
            btn.setAttribute('aria-pressed', String(btn.dataset.locale === current));
        });
    }

    #onAuthRequestComplete = () => {
        this.#setLoading(false);
    };

    #setLoading(value) {
        this.#loading = value;
        const submitBtn = this.$('#submit-btn');
        const toggleLink = this.$('#toggle-mode');
        const status = this.$('#auth-status');

        if (submitBtn) {
            submitBtn.disabled = value;
            this.#setSubmitLabel(this.#submitLabel());
        }
        if (toggleLink) {
            toggleLink.style.pointerEvents = value ? 'none' : '';
            toggleLink.style.opacity = value ? '0.6' : '';
        }
        if (status) {
            if (value) {
                status.classList.remove('hidden', 'slow');
                status.textContent = this.#isRegister ? t('login.statusRegister') : t('login.statusLogin');
            } else {
                status.classList.add('hidden');
                status.classList.remove('slow');
                status.textContent = '';
            }
        }

        this.#clearSlowHint();
        if (value) {
            this.#slowHintTimeoutId = setTimeout(() => {
                const currentStatus = this.$('#auth-status');
                if (!this.#loading || !currentStatus) return;
                currentStatus.classList.add('slow');
                currentStatus.textContent = t('login.slowHint');
            }, 2000);
        }
    }

    #clearSlowHint() {
        if (this.#slowHintTimeoutId) {
            clearTimeout(this.#slowHintTimeoutId);
            this.#slowHintTimeoutId = null;
        }
    }

    #setSubmitLabel(label) {
        const btn = this.$('#submit-btn');
        if (!btn) return;
        const labelSpan = btn.querySelector('span');
        if (labelSpan) {
            labelSpan.textContent = label;
        } else {
            btn.textContent = label;
        }
    }
}

customElements.define('user-login', LoginComponent);
